<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb
          :title="pageTitle"
          :text="pageSection"
          :text1="pageTitle"
        />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap"
        >
          <div class="mb-2">
            <router-link
              class="btn btn-primary d-flex align-items-center"
              :to="{ path: '/performance/performance-review/new' }"
              v-can="'PerformanceReview.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>New Review
            </router-link>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-2 align-items-end">
            <div class="col-md-4">
              <label class="form-label">Search (name / email)</label>
              <input
                v-model="store.filters.search"
                type="text"
                class="form-control"
                placeholder="e.g. Jane Doe, jane@acme.com"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label">Status</label>
              <select class="form-select" v-model="store.filters.status">
                <option value="">All</option>
                <option>Draft</option>
                <option>Submitted</option>
                <option>Reviewed</option>
                <option>Archived</option>
              </select>
            </div>
            <div class="col-md-2 d-grid">
              <button
                class="btn btn-primary"
                @click="store.fetchAll({ page: 1 })"
              >
                Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5 class="d-flex align-items-center">Reviews</h5>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-bordered table-striped mb-0">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Reviewer</th>
                  <th>Appraisal Date</th>
                  <th>Status</th>
                  <th style="width: 180px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="store.loading">
                  <td colspan="5" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="!store.items.length">
                  <td colspan="5" class="text-center py-4">No reviews found</td>
                </tr>
                <tr v-for="r in store.items" :key="r.id">
                  <td>{{ fullName(r.employee?.user || r.employee) }}</td>
                  <td>
                    {{
                      r.reviewer
                        ? fullName(r.reviewer?.user || r.reviewer)
                        : "-"
                    }}
                  </td>
                  <td>{{ formatDate(r.appraisal_date) }}</td>
                  <td>
                    <span class="badge" :class="statusClass(r.status)">{{
                      r.status
                    }}</span>
                  </td>
                  <td class="text-nowrap">
                    <router-link
                      class="btn btn-sm btn-outline-primary me-2"
                      :to="{
                        path: '/performance/performance-review',
                        query: { id: r.id },
                      }"
                      v-can="'PerformanceReview.Edit'"
                    >
                      Edit
                    </router-link>
                    <button
                      class="btn btn-sm btn-outline-secondary me-2"
                      @click="pdf(r.id)"
                      :disabled="store.loadingPdfId === r.id"
                      v-can="'PerformanceReview.Export'"
                    >
                      <span
                        v-if="store.loadingPdfId === r.id"
                        class="spinner-border spinner-border-sm me-1"
                      ></span>
                      PDF
                    </button>

                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click="confirmDelete(r)"
                      v-can="'PerformanceReview.Delete'"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="p-3 d-flex justify-content-between align-items-center">
            <div>
              Showing page {{ store.pagination.current_page }} of
              {{ totalPages }}
            </div>
            <div class="btn-group">
              <button
                class="btn btn-outline-secondary"
                :disabled="store.pagination.current_page <= 1"
                @click="
                  store.fetchAll({ page: store.pagination.current_page - 1 })
                "
              >
                Prev
              </button>
              <button
                class="btn btn-outline-secondary"
                :disabled="store.pagination.current_page >= totalPages"
                @click="
                  store.fetchAll({ page: store.pagination.current_page + 1 })
                "
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between bg-white border-top p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a class="text-primary" href="javascript:void(0);"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { usePerformanceReviewStore } from "@/stores/performanceReviewStore";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const store = usePerformanceReviewStore();
const auth = useAuthStore();

const isEmployee = computed(() => {
  const roles = Array.isArray(auth.user?.roles) ? auth.user.roles : [];
  return roles.some((role) => {
    const name = typeof role === "string" ? role : role?.name;
    return name === "Employee";
  });
});

const pageTitle = computed(() =>
  isEmployee.value ? "My Performance Reviews" : "Performance Reviews"
);
const pageSection = computed(() => (isEmployee.value ? "Employee" : "Performance"));

const totalPages = computed(() => {
  const t = store.pagination.total || 0;
  const p = store.pagination.per_page || 25;
  return Math.max(1, Math.ceil(t / p));
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function fullName(u) {
  if (!u) return "-";
  return `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email || "-";
}
function formatDate(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return v;
  }
}
function statusClass(s) {
  switch (s) {
    case "Draft":
      return "bg-secondary";
    case "Submitted":
      return "bg-info";
    case "Reviewed":
      return "bg-success";
    case "Archived":
      return "bg-dark";
    default:
      return "bg-light text-dark";
  }
}
async function pdf(id) {
  try {
    await store.downloadPdf(id);
  } catch {
    toast("Failed to download PDF", "error");
  }
}
async function confirmDelete(r) {
  if (!confirm("Delete this review?")) return;
  try {
    await store.remove(r.id);
  } catch {}
}

onMounted(() => {
  store.fetchAll();
});
</script>


